import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { useApp } from '../context/AppContext';
import { isOffshoreRow } from '../lib/currency';
import { Input } from './ui/input';
import { Pencil, Check, X, Globe2, RotateCcw } from 'lucide-react';

const fmt2 = (n) => (n ? Number(n).toFixed(2) : '');

/**
 * Manual USD→INR entry for a single (snapshot, source) pair.
 *
 * Each source can carry its own Cost + Mkt Val rate (for offshore holdings).
 * When no per-source rate is set, the snapshot default applies — we surface
 * that fact in the header and give the user a button to override it.
 */
export default function FxRateStrip({ snapshotId, sourceId }) {
  const { snapshots, sources, getFxRate, setFxRate, clearSourceFxRate,
          fallbackFxFromPrior, getHoldings } = useApp();
  const [editing, setEditing] = useState(false);
  const [costDraft, setCostDraft] = useState('');
  const [mvDraft, setMvDraft] = useState('');

  const snap = snapshots.find((s) => s.id === snapshotId);
  const source = sources.find((s) => s.id === sourceId);

  // Per-source rows that decide whether FX even matters
  const rowsHere = snapshotId && sourceId ? getHoldings(snapshotId, sourceId) : [];
  const applicable = rowsHere.some(isOffshoreRow);

  const specific = snapshotId && sourceId ? getFxRate(snapshotId, sourceId) : null;
  const defaultDoc = snapshotId ? getFxRate(snapshotId) : null;    // snapshot-default
  const effective = specific || defaultDoc;
  const storedCost = Number(effective?.usdInrCost || effective?.usdInr || 0);
  const storedMv = Number(effective?.usdInrMv || effective?.usdInr || 0);
  const isUsingDefault = !specific && !!defaultDoc;
  const hasAny = !!effective;

  // Reset drafts when snapshot / source changes or strip closes
  useEffect(() => { setEditing(false); }, [snapshotId, sourceId]);

  if (!snapshotId || !snap || !sourceId || !source) return null;

  // If no offshore rows AND no rate has ever been set, show a compact NA label.
  if (!applicable && !hasAny) {
    return (
      <div className="flex items-center gap-2 text-[11px] font-mono text-[#787872] mb-3" data-testid="fx-strip">
        <Globe2 size={11} />
        <span>USD→INR · {source.name} · NA (no offshore holdings here)</span>
      </div>
    );
  }

  const startEdit = () => {
    const fb = fallbackFxFromPrior(snapshotId, sourceId);
    setCostDraft(storedCost ? String(storedCost) : (fb?.cost ? String(fb.cost) : ''));
    setMvDraft(storedMv ? String(storedMv) : (fb?.mv ? String(fb.mv) : ''));
    setEditing(true);
  };

  const commit = async () => {
    const c = Number(costDraft);
    const m = Number(mvDraft);
    if (!c || c <= 0 || !m || m <= 0) {
      toast.error('Enter positive numbers for both Cost and Mkt Val rates');
      return;
    }
    try {
      await setFxRate(snapshotId, sourceId, { usdInrCost: c, usdInrMv: m });
      toast.success(`FX saved for ${source.name} · ${snap.label}: cost ₹${fmt2(c)}, mkt val ₹${fmt2(m)}`);
      setEditing(false);
    } catch (e) {
      toast.error(e.message || 'Save failed');
    }
  };

  const revertToDefault = async () => {
    if (!specific) return;
    if (!confirm(`Remove the custom FX rate for ${source.name} and use the snapshot default?`)) return;
    try {
      await clearSourceFxRate(snapshotId, sourceId);
      toast.success(`${source.name} now uses the snapshot default FX`);
    } catch (e) {
      toast.error(e.message || 'Could not clear rate');
    }
  };

  const noRate = !storedCost && !storedMv;
  const sameRate = storedCost && storedMv && storedCost === storedMv;

  return (
    <div className="flex items-center gap-3 mb-3 px-3 py-2 border border-border bg-[#FAFAF7] flex-wrap" data-testid="fx-strip">
      <Globe2 size={13} className="text-[hsl(var(--primary))]" />
      <span className="overline">USD → INR · {source.name} · {snap.label}</span>

      {!editing && (
        <>
          {noRate ? (
            <span className="font-mono text-[12px] text-[#9B2C2C]">Not set — please enter rates</span>
          ) : sameRate ? (
            <span className="font-mono text-[12px]">1 USD = ₹{fmt2(storedCost)} (cost &amp; mkt val)</span>
          ) : (
            <span className="font-mono text-[12px]">
              Cost ₹{fmt2(storedCost) || '—'}
              <span className="text-[#787872] mx-1">·</span>
              Mkt Val ₹{fmt2(storedMv) || '—'}
            </span>
          )}
          {isUsingDefault && !noRate && (
            <span className="chip chip-neutral" title="This source is using the snapshot default — click Edit to override">
              default
            </span>
          )}
          {specific && (
            <span className="chip chip-positive" title="Custom rate is set for this source">
              custom
            </span>
          )}
          <div className="ml-auto flex items-center gap-2">
            {specific && (
              <button
                onClick={revertToDefault}
                className="text-[11px] text-[#575757] hover:text-[#9B2C2C] flex items-center gap-1"
                data-testid="fx-strip-revert"
                title="Remove this source-specific rate and revert to the snapshot default"
              >
                <RotateCcw size={11} /> Revert to default
              </button>
            )}
            <button
              onClick={startEdit}
              className="text-[11px] text-[#575757] hover:text-[hsl(var(--primary))] flex items-center gap-1"
              data-testid="fx-strip-edit"
            >
              <Pencil size={11} />
              {noRate ? 'Enter rates' : (isUsingDefault ? 'Override for this source' : 'Edit')}
            </button>
          </div>
        </>
      )}

      {editing && (
        <div className="flex items-end gap-3 ml-auto flex-wrap">
          <div>
            <label className="overline block mb-0.5">Cost rate</label>
            <Input
              type="number" step="0.01" min="0"
              value={costDraft}
              onChange={(e) => setCostDraft(e.target.value)}
              placeholder="83.50"
              className="h-8 w-28 font-mono text-[12px]"
              data-testid="fx-strip-cost-input"
            />
          </div>
          <div>
            <label className="overline block mb-0.5">Mkt val rate</label>
            <Input
              type="number" step="0.01" min="0"
              value={mvDraft}
              onChange={(e) => setMvDraft(e.target.value)}
              placeholder="83.50"
              className="h-8 w-28 font-mono text-[12px]"
              data-testid="fx-strip-mv-input"
            />
          </div>
          <button onClick={commit} className="btn-primary !py-1.5 !px-3 text-[11px] flex items-center gap-1.5" data-testid="fx-strip-commit">
            <Check size={12} /> Save for {source.name}
          </button>
          <button onClick={() => setEditing(false)} className="btn-secondary !py-1.5 !px-2.5 text-[11px] flex items-center gap-1">
            <X size={12} /> Cancel
          </button>
        </div>
      )}
    </div>
  );
}
